<template>
    <div class="info-help">
        <div class="infobody-help">
            <div class="white-header">
                <div class="tenforty-wrap">
                    <router-link :to="$homeLink" class="c-back__box">
                        <span class="c-back__icon">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" width="14px">
                                <path
                                    d="M257.5 445.1l-22.2 22.2c-9.4 9.4-24.6 9.4-33.9 0L7 273c-9.4-9.4-9.4-24.6 0-33.9L201.4 44.7c9.4-9.4 24.6-9.4 33.9 0l22.2 22.2c9.5 9.5 9.3 25-.4 34.3L136.6 216H424c13.3 0 24 10.7 24 24v32c0 13.3-10.7 24-24 24H136.6l120.5 114.8c9.8 9.3 10 24.8.4 34.3z">
                                </path>
                            </svg>
                        </span>
                        <div>Return to info.my.vermont.gov</div>
                    </router-link>
                    <div class="policy-title">MFA Policy Background</div>
                    <p class="policy-subtitle">myVermont connects many State applications through one shared sign-in
                        experience. Strong security protections help the State meet its legal obligations, honor its
                        privacy commitments, and maintain trust with the people and professionals who rely on these
                        systems.</p>

                </div>
            </div>
            <div class="tenforty-wrap">
                <div class="help-two-pane">
                    <div class="help-pane1">


                        <p>

                        </p>

                        <h3>State and Federal Regulatory Requirements</h3>

                        <p>
                            Many applications that rely on myVermont are required to follow federal and state legal
                            requirements. These include, for example:
                        </p>

                        <ul class="policy-list">
                            <li>
                                <strong>IRS Publication 1075</strong> (see section 4.7), which governs safeguards for Federal Tax
                                Information (FTI);
                            </li>
                            <li>
                                <strong>Acceptable Risk Controls for Affordable Care Act, Medicaid, and Partner Entities (ARC-AMPE)</strong> requirements issued by the U.S. Department of Health and Human Services (HHS), Centers for Medicare and Medicaid Services (CMS),
                                which apply to Medicaid and other health-related systems.
                            </li>
                            <li>
                                Although <strong>HIPAA’s Security Rule</strong> is technology-neutral, its requirement to implement
                                “reasonable and appropriate” technical safeguards for access control support the use of MFA as a modern baseline (HHS OCR <a
                                    href="https://www.hhs.gov/hipaa/for-professionals/security/guidance/cybersecurity-newsletter-june-2023/index.html"
                                    target="_blank" rel="noopener">specifically highlights multi-factor authentication
                                    as an effective safeguard in its June 2023 guidance</a>);
                            </li>
                            <li>
                                <strong>Certain Vermont statutes</strong>, such as 9 V.S.A. § 2435 (breach response) or 23 V.S.A. § 104 (incorporating federal Driver’s Privacy Protection Act restrictions), call for the State to keep personal information confidential and prevent
                                breaches of personal information—implying that the State must use
                                reasonable current standards;
                            </li>
                            <li>
                                Some of the laws above either expressly require the State to follow <strong>National Institute of Standards and Technology (NIST)</strong> guidance or require the use of current, reasonable security standards, for which NIST provides the most authoritative framework.
                            </li>
                        </ul>

                        <p>
                            These requirements mandate specific security <em>controls</em>. Some of those controls—such
                            as
                            minimum password standards or multi-factor authentication—can only be applied consistently
                            when they are enforced across all applications that use a shared identity system like
                            myVermont.
                        </p>

                        <h3>Privacy Commitments to Users</h3>

                        <p>
                            In addition to the legal requirements above, <strong>the State of Vermont’s <a href="https://www.vermont.gov/policies/privacy" target="_blank" rel="noopener">Privacy Policy</a></strong> provides assurances to every myVermont user that their information will not be disclosed
                            except for reasonable and ascertainable purposes. This applies to every myVermont
                            application. Strong identity protections help make
                            those assurances meaningful and help users feel safe using State systems.
                        </p>

                        <h3>Protecting the People Behind the Data</h3>

                        <p>
                            myVermont also protects applications used by trusted partners, such as childcare providers,
                            healthcare professionals, and other service providers. These professionals enter sensitive
                            information about children, parents, patients, and other Vermonters into State systems. <strong>Those
                            private individuals must
                            be able to trust that there are basic, minimum protections in place</strong> so that they can feel
                            safe
                            sharing their personal data with essential service providers.
                        </p>

                        <h3>Establishing a Baseline Level of Security</h3>

                        <p>
                            Whether myVermont is trying to meet a specific federal or state law, or simply trying to
                            honor
                            its privacy and security commitments to users and maintain trust in its systems, the State
                            has a responsibility to apply basic minimum controls based on current, known
                            threats.
                            For this reason, <strong>the Agency of Digital Services (ADS) adopted the
                            <a href="https://digitalservices.vermont.gov/sites/digitalservices/files/documents/ADS%20Information%20Security%20Foundations%20Policy.docx.pdf"
                                target="_blank" rel="noopener">
                                ADS Information Security Foundations Policy</a></strong>.
                        </p>

                        <p>
                            For identity and authentication standards, this policy incorporates guidance from NIST by
                            reference, including:
                        </p>

                        <ul class="policy-list">
                            <li>
                                <a href="https://csrc.nist.gov/pubs/sp/800/53/r5/upd1/final" target="_blank"
                                    rel="noopener">
                                    NIST Special Publication 800-53</a>, which defines security controls for both
                                internal users (IA-2) and external users
                                (IA-8); and
                            </li>
                            <li>
                                <a href="https://pages.nist.gov/800-63-3/sp800-63b.html" target="_blank" rel="noopener">
                                    NIST Special Publication 800-63B</a>, which specifies acceptable methods for
                                implementing
                                multi-factor authentication.
                            </li>
                        </ul>

                        <p>
                            NIST calls for multi-factor authentication at Authentication Assurance Level 2 (AAL2)
                            when there is even a moderate risk that compromise of a system could cause harm or cost
                            to an agency or the people it serves. This risk threshold is met for all applications that
                            rely on myVermont.
                        </p>

                        <h3>What This Means for Users</h3>
                        <p>
                            Multi-factor authentication (MFA) is one of the most effective ways to protect online
                            accounts from modern security threats.
                            MFA strengthens security by requiring two or more forms of verification—such as a password
                            plus a code sent to a phone—so that
                            even if a password is stolen, unauthorized access is far less likely.
                        </p>

                        <p>
                            By adding these extra layers of identity verification, MFA helps protect sensitive systems
                            and personal information from common
                            cyberattacks, including phishing and automated attempts to reuse stolen login credentials.
                            Using MFA across myVermont supports
                            the State’s regulatory obligations, privacy commitments, and the trust Vermonters place in
                            the systems they rely on.
                        </p>

                        <ul class="policy-list">
                            <li><strong>Adds security layers:</strong> Goes beyond passwords by combining something you
                                know (a password) with something you have or are, such as a phone or biometric.</li>
                            <li><strong>Mitigates stolen passwords:</strong> Prevents access even if a password is
                                exposed through reuse or a data breach.</li>
                            <li><strong>Blocks credential stuffing:</strong> Stops automated attacks that try large
                                lists of stolen username and password combinations.</li>
                            <li><strong>Reduces phishing risk:</strong> Makes phishing attempts far less effective by
                                requiring additional verification.</li>
                            <li><strong>Protects sensitive data:</strong> Helps ensure that only authorized users can
                                access critical applications and personal information.</li>
                        </ul>



                    </div>
                    <div class="help-pane1">
                        <img src="../../assets/mfaOptions.png" />
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script>
import allHelp from '../allHelp.json'


export default {
    name: 'help-benefits',
    props: ['envLinks'],
    components: {},
    data() {
        return {
            allHelp: allHelp,
            error: ""
        }
    },
    created() {
        const queryString = window.location.search
        const urlParams = new URLSearchParams(queryString)
        this.error = urlParams.get('error')
        console.log(this.error)
    }
}
</script>

<style scoped>
@import './help.css';
</style>
